"use client";

import React, { useEffect, useState } from "react";
import { emitter } from "~/utils/emmitter";
import toggleBodyScrolling from "~/utils/toggleBodyScrolling";
import FlatMap from "./FlatMap";
import LocationDrawer from "./LocationDrawer";
import SubMenuLocations from "./SubMenuLocations";
import { type LocationData } from "~/slices/LocationsSection";

export type LocationType = "Facility" | "Office";

export type Continent =
  | "All facilities"
  | "All locations"
  | "Europe"
  | "USA"
  | "Asia";

export default function MapWrapper({
  locations,
  isLocationOverview = false,
}: {
  locations?: LocationData[];
  isLocationOverview: boolean;
}) {
  // Serves to open type
  const [activeType, setActiveType] = useState<LocationType | null>(null);

  // Serves to draw out the sidedrawer that shows more details on a specific location (will be an ID)
  const [activeLocationName, setActiveLocationName] = useState<string>();

  // Serves to zoom more into a continent on the map, can be activated by clicking on a submenu item or cluster
  // Possible activeContinents: "All locations" | "Europe" | "USA" | "Asia Pacific"
  const [activeContinent, setActiveContinent] =
    useState<Continent>("All facilities");

  // If type is clicked, we want the FlatMap component's filter locations
  function handleTypeClick(type: LocationType) {
    // If already looking at this type, we reset to normal world view, otherwise
    // filter on this type
    if (type === activeType) {
      setActiveType(null);
      emitter.emit("flyToContinent", "All facilities");
      return;
    }

    setActiveType(type);
    setActiveContinent("All facilities");
    setActiveLocationName(undefined);
    const locationTypeToUse =
      (type as string) === "Facilities" ? "Facility" : "Office";
    emitter.emit("showLocationType", locationTypeToUse);
  }

  // TODO: Is stil still needed?
  // Keeps track of total amount of locations that are available, separate variable for readability
  // Grabs all location arrays from continents variable and flattens it to get length of each array and sum it up
  // const totalContinentCount =
  //   locations.map((i) => i?.locations).flat().length || 0;

  // If continent is clicked in either the submenu or through a marker, we want the FlatMap component's map to fly to the location
  const updateContinentHandler = (continent: Continent) => {
    // If clicking the already open continent we go back to the world view.
    const continentToUse =
      continent === activeContinent ? "All facilities" : continent;

    emitter.emit("flyToContinent", continentToUse);
    setActiveContinent(continentToUse);
    setActiveType(null);
  };

  // In case we need it, this resets the continents to "All facilities"
  const resetContinents = () => {
    emitter.emit("flyToContinent", "All facilities");
    setActiveContinent("All facilities");
  };

  // Sets a location if a location marker is clicked, the id will be set and we fetch the locations using the ID.
  const setActiveLocationHandler = (location: string) =>
    setActiveLocationName(location);

  const resetActiveLocation = () => {
    toggleBodyScrolling(false);
    setActiveLocationName(undefined);
  };

  useEffect(() => {
    if (!window) return;
    const url = new URL(window.location.toString());
    const type = url.searchParams.get("type");
    if (type === "Offices") handleTypeClick("Office");
    else if (type === "Facilities") handleTypeClick("Facility");
  }, []);

  return (
    <>
      <SubMenuLocations
        locations={locations}
        activeContinent={activeContinent}
        updateContinentHandler={updateContinentHandler}
        activeType={activeType}
        handleTypeClick={handleTypeClick}
        isLocationOverview={isLocationOverview}
      />
      {locations && (
        <>
          <FlatMap
            locations={locations}
            updateContinentHandler={updateContinentHandler}
            setActiveLocationHandler={setActiveLocationHandler}
            isLocationOverview={isLocationOverview}
          />

          <LocationDrawer
            resetActiveLocation={resetActiveLocation}
            activeLocationName={activeLocationName}
            activeContinent={activeContinent}
            locations={locations}
          />
        </>
      )}
    </>
  );
}
